import { useEffect, useState } from 'react'
import { Clock, AlertCircle } from 'lucide-react'

export interface ResponseCountdownProps {
  /** ISO timestamp of the per-teacher answer deadline (PriorityEntry.timeout_at). */
  deadline?: string | null
  /** 'badge' = compact pill; 'block' = detailed block with absolute time + hint. */
  variant?: 'badge' | 'block'
  size?: 'sm' | 'md'
  className?: string
  /** Optional override for the leading label (default "Antwortfrist"). */
  label?: string
  /** Viewer's own contact window: after expiry the next teacher is asked, this one may still answer. */
  lateAnswerAllowed?: boolean
}

function pad2(n: number): string {
  return n.toString().padStart(2, '0')
}

/** Human-readable remaining time until the deadline. */
function formatRemaining(ms: number): string {
  if (ms <= 0) return 'Frist abgelaufen'
  const totalSec = Math.floor(ms / 1000)
  const h = Math.floor(totalSec / 3600)
  const m = Math.floor((totalSec % 3600) / 60)
  const s = totalSec % 60
  if (h > 0) return `${h}h ${m}m`
  return `${m}m ${pad2(s)}s`
}

/**
 * Live, ticking answer-deadline countdown.
 *
 * Renders nothing when no valid deadline is supplied. With
 * ``lateAnswerAllowed`` an elapsed viewer window stays answerable
 * (timed_out ≠ declined). Without it, expiry is just elapsed time
 * (Wartepool / nächste Person).
 */
export function ResponseCountdown({
  deadline,
  variant = 'badge',
  size = 'md',
  className = '',
  label = 'Antwortfrist',
  lateAnswerAllowed = false,
}: ResponseCountdownProps) {
  const [, setTick] = useState(0)

  const deadlineMs = deadline ? Date.parse(deadline) : NaN

  useEffect(() => {
    if (Number.isNaN(deadlineMs)) return
    let timer: ReturnType<typeof setTimeout>
    const schedule = () => {
      const remainingMs = deadlineMs - Date.now()
      if (remainingMs <= 0) return
      const delay = remainingMs < 10 * 60 * 1000 ? 1000 : 30000
      timer = setTimeout(() => {
        setTick(t => t + 1)
        schedule()
      }, delay)
    }
    schedule()
    return () => clearTimeout(timer)
  }, [deadlineMs])

  if (Number.isNaN(deadlineMs)) return null

  const remaining = deadlineMs - Date.now()
  const expired = remaining <= 0
  const lateOpen = expired && lateAnswerAllowed
  const urgent = !expired && remaining < 10 * 60 * 1000
  const remainingText = formatRemaining(remaining)

  const absTime = new Date(deadlineMs).toLocaleString('de-DE', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })

  const color = lateOpen
    ? 'text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/30 border-orange-200 dark:border-orange-800'
    : expired
      ? 'text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800'
      : urgent
        ? 'text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800'
        : 'text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800'

  const ariaLabel = lateOpen
    ? `${label}: ${absTime} (nächste Person angefragt, Antwort noch möglich)`
    : `${label}: ${absTime}${expired ? ' (abgelaufen)' : ''}`

  const badgeText = lateOpen ? 'Noch möglich' : remainingText
  const title = lateOpen
    ? 'Nächste Person angefragt. Sie können noch antworten.'
    : `${label}: ${absTime}`

  if (variant === 'badge') {
    const sizing = size === 'sm' ? 'text-xs px-2 py-0.5 gap-1' : 'text-xs px-2.5 py-1 gap-1.5'
    return (
      <span
        aria-label={ariaLabel}
        title={title}
        className={`inline-flex items-center rounded-full border font-medium shrink-0 ${color} ${sizing} ${className}`}
      >
        {expired && !lateOpen ? (
          <AlertCircle className="w-3 h-3" aria-hidden="true" />
        ) : (
          <Clock className="w-3 h-3" aria-hidden="true" />
        )}
        <span aria-hidden="true">{badgeText}</span>
      </span>
    )
  }

  return (
    <div
      aria-label={ariaLabel}
      className={`rounded-lg border p-3 ${color} ${className}`}
    >
      <p className="text-xs font-semibold flex items-center gap-1.5">
        {expired && !lateOpen ? (
          <AlertCircle className="w-3.5 h-3.5" aria-hidden="true" />
        ) : (
          <Clock className="w-3.5 h-3.5" aria-hidden="true" />
        )}
        {label}
      </p>
      <p className="text-lg font-bold tabular-nums leading-tight mt-0.5" aria-hidden="true">
        {lateOpen ? 'Nächste Person angefragt' : expired ? 'Abgelaufen' : `Antworten in ${remainingText}`}
      </p>
      <p className="text-xs opacity-80">bis {absTime} Uhr</p>
      {lateAnswerAllowed && (
        <p className="text-[11px] opacity-70 mt-1">
          {expired
            ? 'Sie können verspätet noch antworten.'
            : 'Danach wird die nächste Lehrperson angefragt.'}
        </p>
      )}
    </div>
  )
}
